import React, { useEffect, useState, useCallback } from "react";
import HeartLoader from "@/components/HeartLoader";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Home, MapPin, Users, Star, BedDouble, Bath, Utensils, Zap, Check, Loader2,
  ArrowLeft, Send, Coins, Heart, ShieldCheck,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Price } from "@/components/Price";
import { AMENITIES, ACCOM_LABEL, FOOD_LABEL } from "@/lib/stays";

export default function StayDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, setUser } = useApp();
  const [l, setL] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState(0);
  const [myTours, setMyTours] = useState([]);
  const [req, setReq] = useState({ tour_id: "", city_label: "", check_in: "", nights: 1, guests_count: 1, travelers_desc: "", message: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/transport/stays/${id}`).then(({ data }) => {
      setL(data.listing); setReviews(data.listing.reviews || []);
      setReq((r) => ({ ...r, city_label: data.listing.city_label || "" }));
    }).catch(() => toast.error("Stay not found.")).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user) api.get("/transport/stays/my-tours").then(({ data }) => setMyTours(data.tours)).catch(() => {}); }, [user]);

  const pickTour = (tid) => {
    const t = myTours.find((x) => x.id === tid);
    if (t) setReq((r) => ({ ...r, tour_id: tid, city_label: t.destination_label || r.city_label, check_in: t.start_date || r.check_in, nights: t.days || r.nights }));
    else setReq((r) => ({ ...r, tour_id: "" }));
  };
  const nights = Number(req.nights) || 1;
  const total = l ? (Number(l.price_per_night) || 0) * nights : 0;

  const toggleWishlist = async () => {
    if (!user) { toast.error("Sign in to save this home."); return; }
    try {
      const { data } = await api.post(`/transport/stays/${id}/wishlist`);
      setL((prev) => prev ? { ...prev, is_wishlisted: data.wishlisted } : prev);
      toast.success(data.wishlisted ? "Saved to your wishlist ❤️" : "Removed from wishlist");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!user) { toast.error("Sign in to request a stay."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/transport/stays/${id}/request`, { ...req, nights, guests_count: Number(req.guests_count) || 1 });
      if (l.instant_book) { if (setUser) setUser((u) => u ? { ...u, credits: data.credits_remaining } : u); toast.success("Booked! Your host has been notified. 🎉"); }
      else toast.success("Request sent! Your host will review your profile and accept. 🙌");
      nav("/transport?tab=stays");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><HeartLoader /></div>;
  if (!l) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-sec">Stay not found. <button onClick={() => nav("/transport?tab=stays")} className="text-brand underline">Back to Stays</button></div>;

  const amenityKeys = (l.amenities || []).filter((a) => AMENITIES[a]);

  return (
    <div className="max-w-5xl mx-auto px-4 pt-24 pb-10" data-testid="stay-detail">
      <button onClick={() => nav("/transport?tab=stays")} className="btn-ghost rounded-full h-9 px-3 text-sm flex items-center gap-1.5 mb-4"><ArrowLeft size={15} /> Back to Stays</button>

      {!l.is_mine && (
        <button data-testid="detail-wishlist" onClick={toggleWishlist}
          className={`float-right btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 ${l.is_wishlisted ? "text-rose-400" : ""}`}>
          <Heart size={15} className={l.is_wishlisted ? "fill-rose-400 text-rose-400" : ""} /> {l.is_wishlisted ? "Saved" : "Save"}
        </button>
      )}

      {/* Gallery */}
      <div className="rud-card rounded-2xl overflow-hidden">
        {l.photos?.length > 0 ? (
          <div>
            <img src={l.photos[gallery]} alt={l.home_name} className="w-full h-72 sm:h-96 object-cover" data-testid="detail-photo" />
            {l.photos.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto">
                {l.photos.map((p, i) => (
                  <img key={i} src={p} alt="" onClick={() => setGallery(i)} className={`w-20 h-16 object-cover rounded-lg cursor-pointer shrink-0 ${i === gallery ? "ring-2 ring-brand" : "opacity-70"}`} data-testid={`thumb-${i}`} />
                ))}
              </div>
            )}
          </div>
        ) : <div className="w-full h-72 bg-brand/10 grid place-items-center text-brand"><Home size={48} /></div>}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {/* Left: details */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-serif text-3xl">{l.home_name}</h1>
              {l.instant_book && <span className="text-[11px] text-brand border border-brand/40 rounded-full px-2 py-0.5 flex items-center gap-1"><Zap size={10} /> Instant book</span>}
            </div>
            <div className="text-sec flex items-center gap-1 mt-1"><MapPin size={14} /> {l.city_label}</div>
          </div>

          <div className="flex flex-wrap gap-2 text-sm">
            <span className="px-3 py-1.5 rounded-full bg-elevated border border-rud flex items-center gap-1.5"><BedDouble size={14} /> {ACCOM_LABEL[l.accommodation_type]}</span>
            <span className="px-3 py-1.5 rounded-full bg-elevated border border-rud flex items-center gap-1.5"><Users size={14} /> Up to {l.max_guests} guests</span>
            <span className="px-3 py-1.5 rounded-full bg-elevated border border-rud flex items-center gap-1.5"><BedDouble size={14} /> {l.bedrooms} bd · {l.beds} beds</span>
            <span className="px-3 py-1.5 rounded-full bg-elevated border border-rud flex items-center gap-1.5"><Bath size={14} /> {l.bathrooms} bath</span>
            <span className="px-3 py-1.5 rounded-full bg-elevated border border-rud flex items-center gap-1.5"><Utensils size={14} /> {FOOD_LABEL[l.food]}{l.food_type ? ` · ${l.food_type}` : ""}</span>
          </div>

          {l.summary && <div><h3 className="font-serif text-lg mb-1">About this home</h3><p className="text-sec text-sm whitespace-pre-line">{l.summary}</p></div>}

          {amenityKeys.length > 0 && (
            <div>
              <h3 className="font-serif text-lg mb-2">What's available</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="amenities-list">
                {amenityKeys.map((a) => (
                  <div key={a} className="flex items-center gap-2 text-sm text-sec"><Check size={15} className="text-brand shrink-0" /> {AMENITIES[a]}</div>
                ))}
              </div>
            </div>
          )}

          {l.house_rules && <div><h3 className="font-serif text-lg mb-1">House rules</h3><p className="text-sec text-sm whitespace-pre-line">{l.house_rules}</p></div>}

          {/* Host */}
          <div className="rud-card rounded-2xl p-4">
            <div className="flex items-center gap-3">
              {l.host?.avatar_url ? <img src={l.host.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-brand/15 grid place-items-center text-brand"><Users size={18} /></div>}
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-1.5">Hosted by {l.host?.name}
                  {l.host_rating?.superhost && <span className="text-[10px] text-brand border border-brand/50 rounded-full px-1.5 py-0 flex items-center gap-0.5"><Star size={9} className="fill-brand" /> Superhost</span>}
                </div>
                <div className="text-xs text-sec flex items-center gap-1">
                  {l.host_rating?.count > 0 ? <><Star size={11} className="text-brand fill-brand" /> {l.host_rating.score} ({l.host_rating.count} reviews)</> : "New host"}
                  {l.host?.location ? ` · ${l.host.location}` : ""}
                </div>
              </div>
            </div>
            {l.host?.bio && <p className="text-sm text-sec mt-2">{l.host.bio}</p>}
            {l.host?.interests && <div className="text-xs text-brand mt-2 flex items-center gap-1"><Heart size={12} /> Interests: {l.host.interests}</div>}
            {l.shared_interests?.length > 0 && <div className="text-xs text-brand mt-1">✨ You share {l.shared_interests.length} interest(s): {l.shared_interests.join(", ")}</div>}
          </div>

          {reviews.length > 0 && (
            <div>
              <h3 className="font-serif text-lg mb-2">Reviews</h3>
              <div className="space-y-2">
                {reviews.map((r) => (
                  <div key={r.id} className="rud-card rounded-xl p-3">
                    <div className="flex items-center gap-2 text-sm"><span className="font-medium">{r.reviewer_name}</span><span className="flex items-center gap-0.5 text-brand">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={11} className="fill-brand text-brand" />)}</span></div>
                    {r.comment && <p className="text-sm text-sec mt-1">{r.comment}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: request/book */}
        <div className="lg:col-span-1">
          <div className="rud-card rounded-2xl p-5 lg:sticky lg:top-24" data-testid="detail-request">
            <div className="flex items-baseline justify-between">
              <div className="text-2xl font-serif">{Number(l.price_per_night) > 0 ? <Price usd={l.price_per_night} inline showSub={false} /> : <span className="text-brand">Free</span>}</div>
              <span className="text-xs text-sec">/ night</span>
            </div>
            {l.is_mine ? <p className="text-sm text-sec mt-3">This is your listing.</p> : (
              <div className="space-y-3 mt-4">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-sec">Your travel plan</label>
                  <select data-testid="detail-tour" translate="no" data-no-translate value={req.tour_id} onChange={(e) => pickTour(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-1">
                    <option value="">Enter manually</option>
                    {myTours.map((t) => <option key={t.id} value={t.id}>{t.title} → {t.destination_label} {t.start_date ? `(${t.start_date})` : ""}</option>)}
                  </select>
                </div>
                <input data-testid="detail-city" value={req.city_label} onChange={(e) => setReq({ ...req, city_label: e.target.value })} placeholder="Destination city" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
                <div className="grid grid-cols-3 gap-2">
                  <input data-testid="detail-checkin" type="date" value={req.check_in} onChange={(e) => setReq({ ...req, check_in: e.target.value })} className="rud-input rounded-lg px-2 py-2 text-sm" />
                  <input data-testid="detail-nights" type="number" min={1} value={req.nights} onChange={(e) => setReq({ ...req, nights: e.target.value })} placeholder="Nights" className="rud-input rounded-lg px-2 py-2 text-sm" />
                  <input data-testid="detail-guests" type="number" min={1} max={l.max_guests} value={req.guests_count} onChange={(e) => setReq({ ...req, guests_count: e.target.value })} placeholder="Travelers" className="rud-input rounded-lg px-2 py-2 text-sm" />
                </div>
                <textarea data-testid="detail-travelers" value={req.travelers_desc} onChange={(e) => setReq({ ...req, travelers_desc: e.target.value })} rows={2} placeholder="Who's travelling? (e.g. couple + 1 child, ages, dietary needs, purpose of visit…)" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
                <textarea data-testid="detail-message" value={req.message} onChange={(e) => setReq({ ...req, message: e.target.value })} rows={2} placeholder="A message to your host…" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
                <div className="flex items-center justify-between text-sm pt-1 border-t border-rud"><span className="text-sec">{nights} night(s) × {l.price_per_night || 0}</span><b><Price usd={total} inline showSub={false} /></b></div>
                <button data-testid="detail-submit" onClick={submit} disabled={busy} className="btn-primary w-full h-11 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : l.instant_book ? <Coins size={15} /> : <Send size={15} />} {l.instant_book ? `Pay ${total} & book` : "Send request"}
                </button>
                <p className="text-[11px] text-sec text-center flex items-center justify-center gap-1"><ShieldCheck size={12} className="text-brand" /> {l.instant_book ? "Instant confirmation" : "Charged only after your host accepts"}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
